import type { HelpContent, HelpContentUpdateRequest,
  LoginRequest, RegisterRequest,
  AdminUser, ApplicationSummary, ApplicationStatus,
  TenantSummary, CreateTenantRequest, PatchTenantRequest,
  RoleId, ReviewerApplicationSummary, ReviewScoreItem, UpsertScoreRequest,
  ListSectionsResponse, PrequalificationStatus,
  ProfileResponse, PatchProfileRequest, ChangePasswordRequest,
  AddCompanyRequest, AddCompanyResponse,
  ListQuestionsResponse, PrequalSubmitRequest, PrequalResult,
  SaveDraftRequest, LoadDraftResponse,
  AdminListQuestionsResponse,
  AdminListCompaniesResponse, AdminGetCompanyResponse, PatchCompanyRequest,
  AdminApplicationDetail,
  AdminPrequalificationDetail,
  AdminFormEdit,
  AdminFormEditRequest,
  EmailLogEntry,
  EmailTemplate,
  EmailTemplateUpdateRequest,
  ReviewCriterion,
  DeficiencyRequest,
  DeficiencyCreateRequest,
  ApplicationFormResponse,
  SaveFieldRequest,
  SaveDeclarationsRequest,
  SubmitFormRequest,
  UploadUrlResponse,
  RegisterUploadRequest,
  UploadItem,
  SectionConfigAdmin,
  SectionConfigUpdateRequest,
  SectionConfigCreateRequest,
  DeclarationConfig,
  DeclarationConfigUpdateRequest,
  DeclarationConfigCreateRequest,
  ListDeclarationsResponse,
  DocumentItem,
  DocumentRenameRequest,
  DeadlinesConfig,
  DeadlinesUpdateRequest,
  ScoringRules,
  ScoringRulesUpdateRequest,
  AdminCriterionUpdateRequest,
  AdminSubcriteriaUpdateRequest,
  ReviewerPrequalificationDetail,
} from '@vedjegy/shared';

export interface UploadDownloadItem {
  id: string;
  type: string;
  fileName: string;
  fileSize: number | null;
  mimeType: string | null;
  uploadedAt: string;
  downloadUrl: string | null;
  sectionId: string | null;
}

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

// VITE_TENANT_CODE (set at build time) takes priority — enables single-tenant
// deploys on bare IPs or arbitrary domains where subdomain routing isn't used.
// Falls back to subdomain-based detection for multi-tenant production.
const TENANT_CODE =
  import.meta.env.VITE_TENANT_CODE ||
  (window.location.hostname === 'localhost'
    ? 'gymsmkik'
    : window.location.hostname.split('.')[0]);

// Tenant override for super_admin — set via setTenantOverride()
let _tenantOverride: string | null =
  typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('superAdminTenant') : null;

export function setTenantOverride(code: string | null): void {
  _tenantOverride = code;
  if (typeof sessionStorage !== 'undefined') {
    if (code) sessionStorage.setItem('superAdminTenant', code);
    else sessionStorage.removeItem('superAdminTenant');
  }
}

export function getActiveTenantCode(): string {
  return _tenantOverride ?? TENANT_CODE;
}

interface AuthUser {
  id: string;
  email: string;
  role: string;
  roles?: string[];
  companyId: string | null;
  prequalificationStatus?: PrequalificationStatus;
  isRenewal?: boolean;
}

interface AuthResponse {
  user: AuthUser;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  // Destructure headers so ...rest doesn't overwrite the merged headers object
  const { headers: callerHeaders, ...rest } = options ?? {};
  // Skip Content-Type for FormData — let the browser set multipart boundary automatically
  const isFormData = rest.body instanceof FormData;
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...rest,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      'X-Tenant-Code': getActiveTenantCode(),
      ...callerHeaders,
    },
  });

  if (!res.ok) {
    // Session expired — redirect to login, but only if not already on an auth page
    if (res.status === 401 && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register') && !window.location.pathname.startsWith('/reset-password') && !window.location.pathname.startsWith('/invite/') && !window.location.pathname.startsWith('/staff-invite/') && !window.location.pathname.startsWith('/forgot-password')) {
      window.location.href = '/login';
      return new Promise(() => { /* navigation pending */ }) as Promise<T>;
    }
    const body = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(body?.message ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

/** Build a query string from a params object, omitting undefined values */
function qs(params: Record<string, string | number | boolean | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? '?' + s : '';
}

export const api = {
  // ---------------------------------------------------------------------------
  // Auth
  // ---------------------------------------------------------------------------
  auth: {
    register: (data: RegisterRequest) =>
      request<AuthResponse>('/auth/register', { method: 'POST', body: JSON.stringify(data) }),

    checkRegistry: (data: { taxId: string; email: string; password?: string }) =>
      request<import('@vedjegy/shared').CheckRegistryResponse>('/auth/check-registry', { method: 'POST', body: JSON.stringify(data) }),

    login: (data: LoginRequest) =>
      request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),

    logout: () =>
      request<{ message: string }>('/auth/logout', { method: 'POST' }),

    me: () => request<AuthUser & { roles: string[]; prequalificationStatus?: PrequalificationStatus; isRenewal?: boolean; companyName?: string | null }>('/auth/me'),

    forgotPassword: (data: { email: string }) =>
      request<{ message: string }>('/auth/forgot-password', { method: 'POST', body: JSON.stringify(data) }),

    resetPassword: (data: { token: string; password: string }) =>
      request<{ message: string }>('/auth/reset-password', { method: 'POST', body: JSON.stringify(data) }),
  },

  // ---------------------------------------------------------------------------
  // Admin (tenant_admin + super_admin)
  // ---------------------------------------------------------------------------
  admin: {
    listUsers: (params?: { tenantId?: string }) =>
      request<{ users: AdminUser[] }>('/admin/users' + qs(params ?? {})),

    updateUserRoles: (userId: string, roles: RoleId[]) =>
      request<{ message: string; roles: RoleId[] }>('/admin/users/' + userId + '/roles', {
        method: 'PATCH',
        body: JSON.stringify({ roles }),
      }),

    deactivateUser: (userId: string) =>
      request<{ message: string }>('/admin/users/' + userId, { method: 'DELETE' }),

    updateUserEmail: (userId: string, email: string) =>
      request<{ ok: boolean }>('/admin/users/' + userId + '/email', {
        method: 'PATCH',
        body: JSON.stringify({ email }),
      }),

    getCriteria: () =>
      request<{ criteria: ReviewCriterion[] }>('/admin/criteria'),

    listApplications: (params?: { status?: string; year?: number; type?: string; excellent?: string; search?: string; page?: number; limit?: number }) =>
      request<{ applications: ApplicationSummary[]; pagination: { page: number; limit: number; total: number } }>(
        '/admin/applications' + qs(params ?? {}),
      ),

    setExcellentApproved: (id: string, approved: boolean | null) =>
      request<{ excellentApproved: boolean | null }>('/admin/applications/' + id + '/excellent', {
        method: 'PATCH',
        body: JSON.stringify({ approved }),
      }),

    getApplication: (id: string) =>
      request<AdminApplicationDetail>('/admin/applications/' + id),

    getPrequalification: (appId: string) =>
      request<AdminPrequalificationDetail>('/admin/applications/' + appId + '/prequalification'),

    updateApplicationStatus: (appId: string, status: ApplicationStatus) =>
      request<{ id: string; status: ApplicationStatus }>('/admin/applications/' + appId + '/status', {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),

    assignReviewer: (appId: string, reviewerId: string) =>
      request<{ message: string }>('/admin/applications/' + appId + '/reviewers', {
        method: 'POST',
        body: JSON.stringify({ reviewerId }),
      }),

    unassignReviewer: (appId: string, reviewerId: string) =>
      request<{ message: string }>('/admin/applications/' + appId + '/reviewers/' + reviewerId, {
        method: 'DELETE',
      }),

    getUploads: (appId: string) =>
      request<{ uploads: UploadDownloadItem[] }>('/admin/applications/' + appId + '/uploads'),

    editFormField: (appId: string, data: AdminFormEditRequest) =>
      request<{ saved: boolean; section: string; fieldKey: string; value: string }>(
        '/admin/applications/' + appId + '/form-data',
        { method: 'PATCH', body: JSON.stringify(data) },
      ),

    getFormEdits: (appId: string) =>
      request<{ edits: AdminFormEdit[] }>('/admin/applications/' + appId + '/form-edits'),

    getEmailLog: (params?: { applicationId?: string; event?: string; status?: string; limit?: number; offset?: number }) =>
      request<{ logs: EmailLogEntry[]; pagination: { total: number; limit: number; offset: number } }>(
        '/admin/email-log' + qs(params ?? {}),
      ),

    getEmailTemplates: () =>
      request<{ templates: EmailTemplate[] }>('/admin/email-templates'),

    updateEmailTemplate: (event: string, data: EmailTemplateUpdateRequest) =>
      request<{ ok: boolean; warnings?: string[] }>('/admin/email-templates/' + encodeURIComponent(event), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    resetEmailTemplate: (event: string) =>
      request<{ ok: boolean }>('/admin/email-templates/' + encodeURIComponent(event), {
        method: 'DELETE',
      }),

    getExclusionReview: (appId: string) =>
      request<{ review: import('@vedjegy/shared').ExclusionReview | null }>(
        '/admin/applications/' + appId + '/exclusion-review',
      ),

    decideExclusionReview: (appId: string, data: import('@vedjegy/shared').ExclusionDecideRequest) =>
      request<{ decided: boolean; newStatus: string }>(
        '/admin/applications/' + appId + '/exclusion-review/decide',
        { method: 'POST', body: JSON.stringify(data) },
      ),

    // Returns a raw Response (CSV blob) — caller should handle the download.
    downloadStatistics: (year?: number) =>
      fetch(
        `${BASE_URL}/admin/export/statistics${year ? `?year=${year}` : ''}`,
        { credentials: 'include', headers: { 'X-Tenant-Code': getActiveTenantCode() } },
      ),
  },

  // ---------------------------------------------------------------------------
  // Section config (admin)
  // ---------------------------------------------------------------------------
  sectionConfig: {
    list: (year?: number) =>
      request<{ sections: SectionConfigAdmin[] }>(
        '/config/admin/sections' + (year ? `?year=${year}` : ''),
      ),

    update: (dbId: string, data: SectionConfigUpdateRequest) =>
      request<{ ok: boolean }>('/config/admin/sections/' + dbId, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    create: (data: SectionConfigCreateRequest) =>
      request<{ dbId: string }>('/config/admin/sections', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    delete: (dbId: string) =>
      request<{ ok: boolean }>('/config/admin/sections/' + dbId, {
        method: 'DELETE',
      }),

    reorder: (items: { dbId: string; displayOrder: number }[]) =>
      request<{ ok: boolean }>('/config/admin/sections/reorder', {
        method: 'PUT',
        body: JSON.stringify(items),
      }),
  },


  // ---------------------------------------------------------------------------
  // Review criteria (admin) — per-criterion max_score
  // ---------------------------------------------------------------------------
  reviewCriteria: {
    list: (year: number) =>
      request<{
        year: number;
        criteria: {
          id: string; criterionId: string; label: string;
          maxScore: number; sortOrder: number; rubricType: string;
          subcriteria: { id: string; subcritId: string; label: string; maxScore: number; sortOrder: number }[];
        }[];
      }>(`/config/admin/criteria?year=${year}`),
    update: (id: string, data: AdminCriterionUpdateRequest) =>
      request<{ ok: boolean }>(`/config/admin/criteria/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    updateSubcriteria: (id: string, data: AdminSubcriteriaUpdateRequest) =>
      request<{ ok: boolean }>(`/config/admin/subcriteria/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  },

  // ---------------------------------------------------------------------------
  // Scoring rules (admin)
  // ---------------------------------------------------------------------------
  scoringRules: {
    getYears: () =>
      request<{ years: number[] }>('/config/admin/scoring-rules/years'),

    get: (year: number) =>
      request<ScoringRules>(`/config/admin/scoring-rules?year=${year}`),

    update: (data: ScoringRulesUpdateRequest) =>
      request<{ ok: boolean; id: string }>('/config/admin/scoring-rules', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },

  // ---------------------------------------------------------------------------
  // Declaration config (admin)
  // ---------------------------------------------------------------------------
  declarationConfig: {
    list: () =>
      request<{ declarations: DeclarationConfig[] }>('/config/admin/declarations'),

    listPublic: () =>
      request<ListDeclarationsResponse>('/config/declarations'),

    update: (id: string, data: DeclarationConfigUpdateRequest) =>
      request<{ ok: boolean }>('/config/admin/declarations/' + id, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    create: (data: DeclarationConfigCreateRequest) =>
      request<{ id: string }>('/config/admin/declarations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      request<{ ok: boolean }>('/config/admin/declarations/' + id, {
        method: 'DELETE',
      }),
  },

  // ---------------------------------------------------------------------------
  // Super-admin
  // ---------------------------------------------------------------------------
  superAdmin: {
    listTenants: () =>
      request<{ tenants: TenantSummary[] }>('/super-admin/tenants'),

    createTenant: (data: CreateTenantRequest) =>
      request<{ id: string; code: string; name: string; isActive: boolean }>('/super-admin/tenants', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateTenant: (tenantId: string, data: PatchTenantRequest) =>
      request<{ id: string; code: string; name: string; isActive: boolean }>('/super-admin/tenants/' + tenantId, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    listUsers: (params?: { tenantId?: string; role?: string; page?: number; limit?: number }) =>
      request<{ users: AdminUser[]; pagination: { page: number; limit: number; total: number } }>(
        '/super-admin/users' + qs(params ?? {}),
      ),
  },

  // ---------------------------------------------------------------------------
  // Profile (own user + company)
  // ---------------------------------------------------------------------------
  profile: {
    get: () => request<ProfileResponse>('/profile'),
    update: (data: PatchProfileRequest) =>
      request<{ message: string }>('/profile', { method: 'PATCH', body: JSON.stringify(data) }),
    changePassword: (data: ChangePasswordRequest) =>
      request<{ message: string }>('/profile/password', { method: 'POST', body: JSON.stringify(data) }),

    checkUrl: (url: string) =>
      request<{ reachable: boolean }>('/profile/check-url?' + new URLSearchParams({ url })),
  },

  // ---------------------------------------------------------------------------
  // Companies
  // ---------------------------------------------------------------------------
  companies: {
    add: (data: AddCompanyRequest) =>
      request<AddCompanyResponse>('/companies/add', { method: 'POST', body: JSON.stringify(data) }),
  },

  // ---------------------------------------------------------------------------
  // Config (tenant-scoped, read-only)
  // ---------------------------------------------------------------------------
  config: {
    listSections: () => request<ListSectionsResponse>('/config/sections'),
    listDeclarations: () => request<ListDeclarationsResponse>('/config/declarations'),
    getHelp: () => request<HelpContent>('/config/help'),
    updateHelp: (data: HelpContentUpdateRequest) =>
      request<{ ok: boolean }>('/config/admin/help', { method: 'PUT', body: JSON.stringify(data) }),
    getDeadlines: () => request<DeadlinesConfig>('/config/deadlines'),
    updateDeadlines: (data: DeadlinesUpdateRequest) =>
      request<{ ok: boolean }>('/config/admin/deadlines', { method: 'PUT', body: JSON.stringify(data) }),
    listDocuments: () => request<{ documents: DocumentItem[] }>('/config/documents'),
  },

  // ---------------------------------------------------------------------------
  // Document library (admin)
  // ---------------------------------------------------------------------------
  documents: {
    getUploadUrl: (data: { fileName: string; mimeType: string }) =>
      request<{ docId: string; uploadUrl: string; fileKey: string }>('/admin/documents/upload-url', {
        method: 'POST', body: JSON.stringify(data),
      }),
    register: (data: { docId: string; fileKey: string; displayName: string; fileSize?: number; mimeType?: string }) =>
      request<DocumentItem>('/admin/documents', { method: 'POST', body: JSON.stringify(data) }),
    list: () => request<{ documents: (DocumentItem & { fileKey?: string })[] }>('/admin/documents'),
    rename: (id: string, data: DocumentRenameRequest) =>
      request<{ ok: boolean }>(`/admin/documents/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    remove: (id: string) =>
      request<{ ok: boolean }>(`/admin/documents/${id}`, { method: 'DELETE' }),
  },

  // ---------------------------------------------------------------------------
  // Reviewer
  // ---------------------------------------------------------------------------
  reviewer: {
    listApplications: () =>
      request<{ applications: ReviewerApplicationSummary[] }>('/reviewer/applications'),

    getApplication: (appId: string) =>
      request<{
        id: string; year: number; type: string; status: string;
        companyName: string; taxId: string; submittedAt: string | null;
        formData: Record<string, Record<string, string | null>>;
        prequalification: { total_score: number | null; max_score: number | null; passed: boolean | null } | null;
      }>('/reviewer/applications/' + appId),

    getScores: (appId: string) =>
      request<{ scores: ReviewScoreItem[] }>('/reviewer/applications/' + appId + '/scores'),

    upsertScore: (appId: string, criterionId: string, data: UpsertScoreRequest) =>
      request<{ criterionId: string; score: number; maxScore: number; comment: string | null }>(
        '/reviewer/applications/' + appId + '/scores/' + criterionId,
        { method: 'PUT', body: JSON.stringify(data) },
      ),

    finalizeScores: (appId: string) =>
      request<{ message: string; finalizedCount: number; excellentSuggested: boolean | null }>(
        '/reviewer/applications/' + appId + '/scores/finalize',
        { method: 'POST' },
      ),

    unfinalizeScores: (appId: string) =>
      request<{ message: string; reopenedCount: number }>(
        '/reviewer/applications/' + appId + '/scores/finalize',
        { method: 'DELETE' },
      ),

    getPrequalification: (appId: string) =>
      request<ReviewerPrequalificationDetail>(
        '/reviewer/applications/' + appId + '/prequalification',
      ),

    getNote: (appId: string) =>
      request<{ note: string | null }>('/reviewer/applications/' + appId + '/note'),

    updateNote: (appId: string, note: string | null) =>
      request<{ message: string }>('/reviewer/applications/' + appId + '/note', {
        method: 'PATCH', body: JSON.stringify({ note }),
      }),

    getUploads: (appId: string) =>
      request<{ uploads: UploadDownloadItem[] }>('/reviewer/applications/' + appId + '/uploads'),

    setExcellentApproved: (appId: string, approved: boolean | null) =>
      request<{ excellentApproved: boolean | null }>('/reviewer/applications/' + appId + '/excellent', {
        method: 'PATCH',
        body: JSON.stringify({ approved }),
      }),

    // Returns a Response for streaming as a ZIP file download.
    downloadAttachmentsZip: (appId: string) =>
      fetch(`${BASE_URL}/reviewer/applications/${appId}/attachments-zip`, {
        credentials: 'include',
        headers: { 'X-Tenant-Code': getActiveTenantCode() },
      }),

    getCriteria: (year?: number) =>
      request<{ criteria: ReviewCriterion[] }>('/reviewer/criteria' + (year ? `?year=${year}` : '')),

    createDeficiency: (appId: string, data: DeficiencyCreateRequest) =>
      request<DeficiencyRequest>('/reviewer/applications/' + appId + '/deficiency', {
        method: 'POST', body: JSON.stringify(data),
      }),

    getDeficiency: (appId: string) =>
      request<{ deficiencies: DeficiencyRequest[] }>('/reviewer/applications/' + appId + '/deficiency'),

    reportConflict: (appId: string) =>
      request<{ message: string }>('/reviewer/applications/' + appId + '/conflict', { method: 'POST' }),
  },

  // ---------------------------------------------------------------------------
  // Prequalification
  // ---------------------------------------------------------------------------
  prequalification: {
    listQuestions: () =>
      request<ListQuestionsResponse>('/prequalification/questions'),

    loadDraft: () =>
      request<LoadDraftResponse>('/prequalification/draft'),

    saveDraft: (data: SaveDraftRequest) =>
      request<{ saved: boolean }>('/prequalification/draft', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    submit: (data: PrequalSubmitRequest) =>
      request<PrequalResult>('/prequalification/submit', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // ---------------------------------------------------------------------------
  // Admin prequalification management (tenant_admin + super_admin)
  // ---------------------------------------------------------------------------
  adminPrequalification: {
    listQuestions: () =>
      request<AdminListQuestionsResponse>('/admin/prequalification/questions'),
  },

  // ---------------------------------------------------------------------------
  // Application form (company_user)
  // ---------------------------------------------------------------------------
  applicationForm: {
    load: () =>
      request<ApplicationFormResponse>('/application/form'),

    saveField: (data: SaveFieldRequest) =>
      request<{ saved: boolean }>('/application/form/data', {
        method: 'PUT', body: JSON.stringify(data),
      }),

    saveDeclarations: (data: SaveDeclarationsRequest) =>
      request<{ saved: boolean }>('/application/form/declarations', {
        method: 'PUT', body: JSON.stringify(data),
      }),

    getUploadUrl: (params: { type: string; fileName: string; mimeType: string }) =>
      request<UploadUrlResponse>(
        '/application/form/upload-url?' + new URLSearchParams(params as Record<string, string>),
      ),

    registerUpload: (data: RegisterUploadRequest) =>
      request<UploadItem>('/application/form/uploads', {
        method: 'POST', body: JSON.stringify(data),
      }),

    deleteUpload: (id: string) =>
      request<{ deleted: boolean }>('/application/form/uploads/' + id, { method: 'DELETE' }),

    getUploadDownloadUrl: (id: string) =>
      request<{ url: string }>('/application/form/uploads/' + id + '/download'),

    submit: (data: SubmitFormRequest) =>
      request<{ submitted: boolean }>('/application/form/submit', {
        method: 'POST', body: JSON.stringify(data),
      }),

    savePublicationChoice: (usePreviousPublication: boolean) =>
      request<{ saved: boolean }>('/application/form/publication-choice', {
        method: 'PATCH', body: JSON.stringify({ usePreviousPublication }),
      }),

    checkUrl: (url: string) =>
      request<{ reachable: boolean }>('/application/form/check-url?url=' + encodeURIComponent(url)),

    getDeficiency: () =>
      request<{ deficiency: DeficiencyRequest | null }>('/application/form/deficiency'),

    deficiencySubmit: (data?: { response?: string; sectionResponses?: Record<string, string> }) =>
      request<{ submitted: boolean }>('/application/form/deficiency-submit', { method: 'POST', body: JSON.stringify(data ?? {}) }),
  },

  adminCompanies: {
    list: () =>
      request<AdminListCompaniesResponse>('/admin/companies'),

    get: (id: string) =>
      request<AdminGetCompanyResponse>('/admin/companies/' + id),

    patch: (id: string, data: PatchCompanyRequest) =>
      request<{ message: string }>('/admin/companies/' + id, {
        method: 'PATCH', body: JSON.stringify(data),
      }),

    setRenewal: (id: string, isRenewal: boolean) =>
      request<{ message: string }>('/admin/companies/' + id + '/renewal', {
        method: 'PATCH', body: JSON.stringify({ isRenewal }),
      }),

    inviteNew: (data: { email: string; taxId: string }) =>
      request<{ message: string; inviteLink: string; companyName: string }>(
        '/admin/companies/invite-new',
        { method: 'POST', body: JSON.stringify(data) },
      ),

    inviteUser: (companyId: string, email: string) =>
      request<{ message: string }>('/admin/companies/' + companyId + '/invite', {
        method: 'POST', body: JSON.stringify({ email }),
      }),

    addUser: (companyId: string, userId: string) =>
      request<{ message: string }>('/admin/companies/' + companyId + '/users/' + userId, {
        method: 'PUT',
      }),

    removeUser: (companyId: string, userId: string) =>
      request<{ message: string }>('/admin/companies/' + companyId + '/users/' + userId, {
        method: 'DELETE',
      }),
  },

  chamberRegistry: {
    list: () =>
      request<{ entries: import('@vedjegy/shared').ChamberRegistryEntry[] }>('/admin/chamber-registry'),

    import: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<import('@vedjegy/shared').ChamberRegistryImportResult>(
        '/admin/chamber-registry/import',
        { method: 'POST', body: form, headers: {} },
      );
    },

    patch: (id: string, data: {
      taxId?: string; email?: string; email2?: string | null; isRenewal?: boolean;
      companyName?: string | null; hqZip?: string | null; hqCity?: string | null;
      hqStreet?: string | null; memberSince?: string | null;
      chapter?: string | null; region?: string | null;
    }) =>
      request<{ message: string }>('/admin/chamber-registry/' + id, { method: 'PATCH', body: JSON.stringify(data) }),

    delete: (id: string) =>
      request<{ message: string }>('/admin/chamber-registry/' + id, { method: 'DELETE' }),
  },

  invite: {
    send: (email: string) =>
      request<{ message: string }>('/invite', { method: 'POST', body: JSON.stringify({ email }) }),

    getInfo: (token: string) =>
      request<import('@vedjegy/shared').InviteInfo>('/invite/' + token),

    accept: (token: string, password: string) =>
      request<{ message: string }>('/invite/' + token + '/accept', {
        method: 'POST', body: JSON.stringify({ password }),
      }),
  },

  staffInvite: {
    send: (data: { email: string; role: 'tenant_admin' | 'reviewer' | 'editor' }) =>
      request<{ message: string; inviteLink: string }>('/admin/staff-invites', { method: 'POST', body: JSON.stringify(data) }),

    list: (params?: { status?: string; limit?: number; offset?: number }) =>
      request<{ invites: import('@vedjegy/shared').StaffInviteEntry[]; total: number }>(
        '/admin/staff-invites' + (params ? '?' + new URLSearchParams(
          Object.fromEntries(Object.entries(params).filter(([,v]) => v !== undefined).map(([k,v]) => [k, String(v)]))
        ) : ''),
      ),

    getInfo: (token: string) =>
      request<import('@vedjegy/shared').StaffInviteInfo>('/staff-invite/' + token),

    accept: (token: string, password: string) =>
      request<{ message: string }>('/staff-invite/' + token + '/accept', {
        method: 'POST', body: JSON.stringify({ password }),
      }),
  },

  auditLog: {
    list: (limit = 100, offset = 0) =>
      request<{ entries: import('@vedjegy/shared').AuditLogEntry[]; total: number }>(
        `/admin/audit-log?limit=${limit}&offset=${offset}`,
      ),
  },

  adminInvites: {
    list: (params?: { status?: string; limit?: number; offset?: number }) =>
      request<{ invites: import('@vedjegy/shared').AdminInviteEntry[]; total: number }>(
        '/admin/invites' + (params ? '?' + new URLSearchParams(
          Object.fromEntries(Object.entries(params).filter(([,v]) => v !== undefined).map(([k,v]) => [k, String(v)]))
        ) : ''),
      ),
  },

  ai: {
    triggerSummary: (applicationId: string) =>
      request<{ summaryId: string }>('/ai-summary/' + applicationId, { method: 'POST' }),

    getSummary: (applicationId: string) =>
      request<{ summary: import('@vedjegy/shared').AiSummary }>('/ai-summary/' + applicationId),

    acceptScores: (applicationId: string, data: import('@vedjegy/shared').AcceptAiScoresRequest) =>
      request<{ sectionId: string; accepted: boolean }>(
        '/ai-summary/' + applicationId + '/accept-scores',
        { method: 'POST', body: JSON.stringify(data) },
      ),
  },

  aiConfig: {
    get: () =>
      request<{ config: import('@vedjegy/shared').AiConfig }>('/ai-config'),

    update: (data: import('@vedjegy/shared').AiConfig) =>
      request<{ ok: boolean }>('/ai-config', { method: 'PUT', body: JSON.stringify(data) }),
  },

  adminAuditLog: {
    list: (params?: { limit?: number; offset?: number }) =>
      request<{ entries: import('@vedjegy/shared').AuditLogEntry[]; total: number }>(
        '/admin/audit-log' + (params ? '?' + new URLSearchParams(
          Object.fromEntries(Object.entries(params).filter(([,v]) => v !== undefined).map(([k,v]) => [k, String(v)]))
        ) : ''),
      ),
  },

  editor: {
    getPublicationList: () =>
      request<{ applications: Array<{
        id: string;
        companyName: string;
        applicationType: string;
        usePreviousPublication: boolean;
      }> }>('/editor/publication-list'),

    // Returns a Response so the caller can stream it directly as a file download.
    // Must include X-Tenant-Code because the raw fetch bypasses the request() helper.
    downloadPublicationZip: () =>
      fetch(`${BASE_URL}/editor/publication-zip`, {
        credentials: 'include',
        headers: { 'X-Tenant-Code': getActiveTenantCode() },
      }),
  },

  compliance: {
    get: (applicationId: string) =>
      request<import('@vedjegy/shared').ComplianceCheckResponse>(
        '/admin/applications/' + applicationId + '/compliance',
      ),
    recheck: (applicationId: string) =>
      request<{ message: string }>(
        '/admin/applications/' + applicationId + '/compliance-recheck',
        { method: 'POST' },
      ),
    getForReviewer: (applicationId: string) =>
      request<import('@vedjegy/shared').ComplianceCheckResponse>(
        '/reviewer/applications/' + applicationId + '/compliance',
      ),
    recheckForReviewer: (applicationId: string) =>
      request<{ message: string }>(
        '/reviewer/applications/' + applicationId + '/compliance-recheck',
        { method: 'POST' },
      ),
  },
};
